import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import type { IntegrationPartner } from '../types';

const IntegrationSection: React.FC = () => {
  const integrationPartners: IntegrationPartner[] = [
  {
    id: 'aws',
    name: 'Amazon Web Services',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee30d118-1764487569257.png",
    alt: 'Amazon Web Services AWS logo with orange and white cloud computing platform branding',
    category: 'Cloud Infrastructure',
    description: 'Native integration with AWS services including S3, Lambda, and CloudWatch'
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18fe6e4ba-1764487569242.png",
    alt: 'Microsoft Azure logo with blue gradient cloud platform branding and modern design',
    category: 'Cloud Infrastructure',
    description: 'Seamless deployment on Azure with support for Azure IoT and Cognitive Services'
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8e34304-1764487569254.png",
    alt: 'Salesforce CRM logo with blue cloud icon representing customer relationship management platform',
    category: 'CRM',
    description: 'Direct integration with Salesforce CRM for customer analytics and insights'
  },
  {
    id: 'slack',
    name: 'Slack',
    logo: "https://images.unsplash.com/photo-1496200186974-4293800e2c20",
    alt: 'Slack communication platform logo with colorful hashtag symbol and modern workspace branding',
    category: 'Communication',
    description: 'Real-time alerts and notifications through Slack channels'
  },
  {
    id: 'tableau',
    name: 'Tableau',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d01894ce-1764487567655.png",
    alt: 'Tableau data visualization logo with blue and white analytics platform branding',
    category: 'Analytics',
    description: 'Export analytics data to Tableau for advanced visualization and reporting'
  },
  {
    id: 'power-bi',
    name: 'Power BI',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1483b2c49-1764487568185.png",
    alt: 'Microsoft Power BI logo with yellow and black business intelligence platform branding',
    category: 'Analytics',
    description: 'Connect analytics data to Power BI dashboards and reports'
  }];


  const integrationCategories = [
  { name: 'Cloud Infrastructure', count: 2, icon: 'Cloud' },
  { name: 'CRM', count: 1, icon: 'Users' },
  { name: 'Communication', count: 1, icon: 'MessageSquare' },
  { name: 'Analytics', count: 2, icon: 'BarChart' }];


  return (
    <section className="py-20 bg-gradient-to-b from-background to-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Integration Ecosystem
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Connect with your existing tools and platforms through our comprehensive integration marketplace
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {integrationCategories.map((category, index) =>
          <div
            key={index}
            className="bg-card rounded-lg p-4 shadow-brand border border-border text-center hover:border-primary/50 transition-all duration-300">

              <div className="flex justify-center mb-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={category.icon} size={24} className="text-primary" />
                </div>
              </div>
              <div className="font-semibold text-foreground mb-1">{category.name}</div>
              <div className="text-sm text-text-secondary">{category.count} Integrations</div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {integrationPartners.map((partner) =>
          <div
            key={partner.id}
            className="bg-card rounded-lg p-6 shadow-brand border border-border hover:border-primary/50 transition-all duration-300">

              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 overflow-hidden rounded-lg border border-border">
                  <Image
                  src={partner.logo}
                  alt={partner.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-foreground mb-1">{partner.name}</h3>
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    {partner.category}
                  </span>
                </div>
              </div>
              <p className="text-sm text-text-secondary mb-4">{partner.description}</p>
              <Button variant="outline" size="sm" fullWidth iconName="ExternalLink" iconPosition="right">
                View Integration
              </Button>
            </div>
          )}
        </div>

        <div className="bg-card rounded-lg p-8 shadow-brand border border-border">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Custom Integrations</h3>
              <p className="text-text-secondary mb-4">
                Build custom integrations using our comprehensive REST API and webhooks. Our developer-friendly documentation and SDKs make integration straightforward.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Icon name="Code" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">RESTful API with OpenAPI specification</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Webhook" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Real-time webhooks for event notifications</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Package" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">SDKs for Python, JavaScript, and Java</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Developer Resources</h3>
              <p className="text-text-secondary mb-4">
                Access comprehensive documentation, code examples, and developer tools to accelerate your integration development.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Icon name="Book" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Interactive API documentation</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Terminal" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">API testing sandbox environment</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Users" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Developer community and support</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 pt-8 border-t border-border">
            <Button variant="default" iconName="Code" iconPosition="left">
              Explore API
            </Button>
            <Button variant="outline" iconName="Book" iconPosition="left">
              Developer Docs
            </Button>
            <Button variant="outline" iconName="MessageSquare" iconPosition="left">
              Join Community
            </Button>
          </div>
        </div>
      </div>
    </section>);

};

export default IntegrationSection;